import './Toolbar.scss';

import { BaseEntityEvent, BaseEvent, ListenerHandle, } from '@projectstorm/react-canvas-core';
import { LinkModel, LinkModelGenerics, NodeModel, NodeModelGenerics, } from '@projectstorm/react-diagrams';
import { Button } from 'primereact/button';
import { Skeleton } from 'primereact/skeleton';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ITopology } from '../../../app/apiSlice';
import { setToast } from '../../../app/appSlice';
import useHandleExport from '../../../hooks/handleExport';
import { useAppDispatch, useAppSelector } from '../../../hooks/hooks';
import { usePrompt } from '../../../hooks/useBlocker';
import { useGraph } from '../../../hooks/useGraph';
import { useKeyDown } from '../../../hooks/useKey';
import { changeSidebarPane, setChangedTopology } from '../editorSlice';
import { useSecureApplication } from '../../../hooks/KeycloackConnection';
import useLinkChecker from '../../../hooks/useLinkCheckerGraph';
import { useLazyGetCatalogQuery } from '../../libraries/DataLibrarySlice';
import {useLazyGetTopologyVersionsQuery} from "../../../app/apiSlice";
import DeleteDiagram from "./DeleteDiagram";
import { useSavePipeline } from '../../../hooks/useSavePipeline';

interface ToolbarProps {
  topology: ITopology;
  onButtonClick?: (buttonId: string) => void;
}

const warningNotSaveMsg = 'editor.not-save-warning';

export const Toolbar = ({topology, onButtonClick}: ToolbarProps) => {
  const { t } = useTranslation();
  const { render, save, engine } = useGraph();
  const { savePipeline, saveStatus } = useSavePipeline();
  const dispatch = useAppDispatch();
  const pipelineChanged = useAppSelector((state) => state.editor.changedTopology);
  const { hasAnyPerm } = useSecureApplication();

  const [getVersions, { data, ...status }] = useLazyGetTopologyVersionsQuery();
  const topologyRedux = useAppSelector((state) => state.editor.current);
  const { handleExport } = useHandleExport();
  const { unauthorizedLink, unauthorizedLoop } = useLinkChecker();
  const [ getCatalog, { data: catalog } ] = useLazyGetCatalogQuery();

  useKeyDown(
    (e: KeyboardEvent) => {
      e.preventDefault();
      savePipeline(topology, catalog, topology?.version);
      getVersions({ id: topology?.id });
    },
    ['s', 'S'],
    true
  );

  useEffect(() => {
    if (topologyRedux && !catalog) getCatalog({runtimeType:topologyRedux.runtimeMode},true)
  }, [topologyRedux]);

  //Pipeline changed in the graph only when :  link, node or  settings are updated
  const refreshListener = (event: BaseEvent & { function: string }) => {
      if (
      event.function === "linksUpdated" ||
      event.function === "entityRemoved" ||
      // event.function === "targetPortChanged" ||
      event.function === "SettingsUpdated" ||
      event.function === "NodesUpdated" ||
      event.function === "NodeIsDisabled"
    ) {
      if (!pipelineChanged) {
        //@ts-ignore
        dispatch(setChangedTopology(true));
       }
    }
  };

  // Register listeners on Diagram event to refresh
  // `pipelineChanged` boolean when user edit the pipeline.
  // (We deregister + register again all listeners on every `lastPipelinedSaved`
  // and `runtimes` change to update the listeners's scope (in which this 2 vars are))
  // Init `lastPipelinedSaved`
  useEffect(() => {
    const registeredListeners: ListenerHandle[] = [];


    const addRefreshListenerToNodeAndPort = (
      node: NodeModel<NodeModelGenerics>
    ) => {
      // @ts-ignore (couldn't find right way to write listener's parameter's type)
      const l = node.registerListener({
        eventDidFire: refreshListener,
      });
      registeredListeners.push(l);

      // Add listener to all Ports of this Node
      Object.values(node.getPorts()).forEach((port) => {
        // @ts-ignore (couldn't find right way to write listener's parameter's type)
        const l = port.registerListener({
          eventDidFire: refreshListener,
        });
        registeredListeners.push(l);
      });
    };

    const addRefreshListenerToLink = (link: LinkModel<LinkModelGenerics>) => {
      // @ts-ignore (couldn't find right way to write listener's parameter's type)
      link.registerListener({
        eventDidFire: refreshListener,
      });
    };

    if (engine !== undefined) {
      // @ts-ignore (couldn't find right way to write listener's parameter's type)
      const l = engine.getModel().registerListener({
        eventDidFire: refreshListener,

        // Add listener to new Node
        nodesUpdated: (
          e: BaseEntityEvent & { node: NodeModel; isCreated: boolean }
        ) => {
          if (e.isCreated) {
            addRefreshListenerToNodeAndPort(e.node);
          }
        },

        // Add listener to new Link
        linksUpdated: (
          e: BaseEntityEvent & { link: LinkModel; isCreated: boolean }
        ) => {
          if (e.isCreated) {
            addRefreshListenerToLink(e.link);
            if (e.link.getOptions().id) {
              unauthorizedLink(e.link, topologyRedux);
            }
          }
          e.link.registerListener({
            targetPortChanged: () => {
              unauthorizedLoop(e.link);
            }
          });
          registeredListeners.push(l);
        }
      });
      registeredListeners.push(l);

      // Add listener to all Node already in diagram
      engine.getModel().getNodes().forEach(addRefreshListenerToNodeAndPort);
      // Add listener to all Link already in diagram
      engine.getModel().getLinks().forEach(addRefreshListenerToLink);
    }

    return () => {
      //unRegister Listeners to avoid memory leak
      registeredListeners.forEach((l) => l.deregister());
    };
  }, [engine,topologyRedux]);

  // If pipeline has been modified and not saved, set warning message
  // to be displayed to user if he try to close or refresh the windows (using `onbeforeunload`)
  useEffect(() => {
    if (pipelineChanged) {
      // (Most modern browser do not allow custom message, a generic
      // one will surely be used instead of `t(warningNotSaveMsg)`)
      window.onbeforeunload = () => t(warningNotSaveMsg);
    } else {
      // @ts-ignore
      window.onbeforeunload = undefined;
    }
  }, [pipelineChanged]);

  // Set the same message (as useEffect above) when
  // changing page using react-router
  usePrompt(t(warningNotSaveMsg), pipelineChanged);


  if (!topology) {
    return (
      <div className="toolbar">
        <div className="flex justify-content-between">
          <div className="toolbar-pipeline-name flex flex-grow-1 flex-column">
            <div className="p-2">
              <Skeleton width={ '100px' }/>
            </div>
            <div className="p-2">
              <Skeleton width={ '100px' }/>
            </div>
          </div>
          <div className="flex-grow-1"/>
          <div className="flex flex-none toolbar-pipeline-name"></div>
        </div>
      </div>
    );
  }

  const ExportButton = () => {
    return (
      <Button
        className="p-button-text"
        icon="pi pi-download"
        title={'Télécharger'}
        onClick={ () => {
          if (topologyRedux) {
            let flowRender = undefined;
            if (catalog) flowRender = render({
              hidePassword: false,
              catalog: catalog
            });

            if (!flowRender) {
              throw Error('Error while getting graph');
            }
            const cpuLimit = topologyRedux.cpuLimit ?? topology.cpuLimit;
            const cpuRequest = topologyRedux.cpuRequest ?? topology.cpuRequest;
            const memoryLimit = topologyRedux.memoryLimit ?? topology.memoryLimit;
            const memoryRequest = topologyRedux.memoryRequest ?? topology.memoryRequest;
            const schedule = topologyRedux.schedule || topologyRedux.schedule === "" ? topologyRedux.schedule : topology.schedule;

            handleExport({...topologyRedux,cpuLimit:cpuLimit,cpuRequest:cpuRequest,memoryLimit:memoryLimit,memoryRequest:memoryRequest,data:JSON.stringify(flowRender),graph:save(),schedule});
          } else {
            dispatch(
              setToast({
                severity: 'error',
                content: `Erreur dans l'export de ${ topology.name }`,
                life: 3000,
              })
            );
          }
        } }
      >
      </Button>
    );
  };

  return (
    <div className="toolbarTreatment">
      <div className="flex">
        <div className="flex background">
          <DeleteDiagram />
          <Button
            className="p-button-text"
            title={ 'Code' }
            onClick={ (_) => {
              onButtonClick && onButtonClick('code');
            } }
            icon={"pi pi-code"}
          />
          <Button
            className="p-button-text"
            title={ 'Paramètres' }
            icon="pi pi-cog"
            disabled={ !hasAnyPerm('pipelines.all', 'pipelines_upd') }
            onClick={ () => {
              dispatch(
                changeSidebarPane({ pane: 'settings', arg: topology.id })
              )
            }
            }
          />
          <Button
              className="p-button-text"
              title={ 'Flux lancés' }
              icon="pi pi-spinner"
              onClick={ () => {
                dispatch(
                    changeSidebarPane({ pane: 'flows', arg: {topologyID: topology.id, topologyVersion: topology.version} })
                )
              }
              }
          />
          <Button
              className="p-button-text"
              title={ 'Versions' }
              icon="pi pi-list"
              onClick={ () => {
                dispatch(
                    changeSidebarPane({ pane: 'versions', arg: {topology: topology, catalog: catalog} })
                )
              }
              }
          />

          { ExportButton() }
        </div>
      </div>
    </div>
  );
};
